import React, {useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {ArrowLeft, Download} from 'lucide-react'
import saveAs from 'file-saver'
import {
  getGoogleDrivePreviewUrl,
  getNativeAppAuthToken,
  normalizeGoogleDriveUrl,
} from 'utils/ConstFunctions'

const PDF_MIME_TYPE = 'application/pdf'

type PdfDownloadHandler = (params: {
  url: string
  fileName: string
}) => Promise<boolean | void> | boolean | void

interface PDFWebviewProps {
  pdfUrl: string
  onBack: () => void
  fileName?: string
  preferDataUrlPreviewOnIOS?: boolean
  loadPdfPreviewBlob?: (url: string) => Promise<Blob>
  onDownload?: PdfDownloadHandler
}

const isIOSPdfBrowser = () => {
  const userAgent = window.navigator.userAgent || ''
  const platform = window.navigator.platform || ''

  return (
    /iPhone|iPad|iPod/i.test(userAgent) ||
    /iPhone|iPad|iPod/i.test(platform) ||
    (platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
  )
}

const isMobilePdfBrowser = () => {
  const userAgent = window.navigator.userAgent || ''
  const platform = window.navigator.platform || ''
  const isNarrowMobileViewport = window.matchMedia?.('(max-width: 767px)')?.matches ?? false

  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(userAgent) ||
    /iPhone|iPad|iPod/i.test(platform) ||
    (platform === 'MacIntel' && window.navigator.maxTouchPoints > 1) ||
    isNarrowMobileViewport
  )
}

const ensurePdfBlob = (sourceBlob: Blob) =>
  sourceBlob.type === PDF_MIME_TYPE ? sourceBlob : new Blob([sourceBlob], {type: PDF_MIME_TYPE})

const blobToDataUrl = async (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('Unable to prepare PDF preview'))
    reader.readAsDataURL(blob)
  })

const sendPdfBytesToNativeApp = async (url: string, resolvedFileName: string) => {
  const reactNativeWebView = (window as any).ReactNativeWebView
  if (!reactNativeWebView || !isIOSPdfBrowser()) return false

  const pdfBlob = await fetchPdfBlob(url)
  const fileBase64 = await blobToDataUrl(pdfBlob)

  reactNativeWebView.postMessage(
    JSON.stringify({
      file_name: resolvedFileName,
      file_type: PDF_MIME_TYPE,
      file_base64: fileBase64,
    })
  )

  return true
}

const getFetchCredentials = (url: string): RequestCredentials => {
  try {
    const parsedUrl = new URL(url, window.location.href)
    return parsedUrl.origin === window.location.origin ? 'include' : 'omit'
  } catch {
    return 'include'
  }
}

const shouldAttachAuthHeader = (url: string) => {
  try {
    const hostname = new URL(url, window.location.href).hostname.toLowerCase()
    return [
      'core-service-stage.up.railway.app',
      'core-service-production-8d72.up.railway.app',
      'dental-stack.com',
      'smilezy.com',
      'routetosmile.com',
      'aiiqaligner.com',
    ].some((domain) => hostname === domain || hostname.endsWith(`.${domain}`))
  } catch {
    return false
  }
}

const getPdfFetchHeaders = (url: string): HeadersInit | undefined => {
  const token = getNativeAppAuthToken()
  if (!token || !shouldAttachAuthHeader(url)) return undefined

  return {
    Authorization: token.toLowerCase().startsWith('bearer ') ? token : `Bearer ${token}`,
  }
}

const fetchPdfBlob = async (url: string) => {
  const primaryCredentials = getFetchCredentials(url)
  const credentialAttempts: RequestCredentials[] =
    primaryCredentials === 'include' ? ['include'] : ['omit', 'include']

  let lastError: unknown

  for (const credentials of credentialAttempts) {
    try {
      const response = await fetch(url, {
        credentials,
        headers: getPdfFetchHeaders(url),
      })
      if (!response.ok) {
        throw new Error(`Failed to load PDF: ${response.status}`)
      }

      const sourceBlob = await response.blob()
      return ensurePdfBlob(sourceBlob)
    } catch (error) {
      lastError = error
    }
  }

  throw lastError
}

const openDirectPdf = (url: string, resolvedFileName: string) => {
  const reactNativeWebView = (window as any).ReactNativeWebView

  reactNativeWebView?.postMessage(
    JSON.stringify({
      file_details: [
        {
          url,
          name: resolvedFileName,
          type: PDF_MIME_TYPE,
          extension: 'pdf',
          auth_token: getNativeAppAuthToken(),
        },
      ],
    })
  )

  if (reactNativeWebView && isIOSPdfBrowser()) return

  const link = document.createElement('a')
  link.href = url
  link.download = resolvedFileName
  link.rel = 'noopener noreferrer'
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const PDFWebview: React.FC<PDFWebviewProps> = ({
  pdfUrl,
  onBack,
  fileName,
  preferDataUrlPreviewOnIOS = true,
  loadPdfPreviewBlob,
  onDownload,
}) => {
  const portalRoot = document.getElementById('root') || document.body
  const [isDownloading, setIsDownloading] = useState(false)
  const [isViewerLoading, setIsViewerLoading] = useState(false)
  const [viewerUrl, setViewerUrl] = useState('')
  const [viewerLoadFailed, setViewerLoadFailed] = useState(false)
  const shouldUseIOSDataUrlPreview = preferDataUrlPreviewOnIOS && isIOSPdfBrowser()
  const shouldUseNativeIOSPreview = Boolean((window as any).ReactNativeWebView) && isIOSPdfBrowser()
  const didOpenNativePreviewRef = useRef(false)

  const getResolvedFileName = () => {
    const trimmedName = fileName?.trim()
    if (!trimmedName) return 'document.pdf'
    return trimmedName.toLowerCase().endsWith('.pdf') ? trimmedName : `${trimmedName}.pdf`
  }

  const downloadTarget = useMemo(() => normalizeGoogleDriveUrl(pdfUrl), [pdfUrl])
  const viewerSource = useMemo(
    () =>
      shouldUseIOSDataUrlPreview
        ? normalizeGoogleDriveUrl(pdfUrl)
        : getGoogleDrivePreviewUrl(pdfUrl),
    [pdfUrl, shouldUseIOSDataUrlPreview]
  )
  const iframeAllow = isMobilePdfBrowser() ? undefined : 'fullscreen'

  const getPdfViewerUrl = (url: string) => {
    if (!url || url.startsWith('blob:') || url.startsWith('data:')) return url
    if (url.includes('drive.google.com') && url.includes('/preview')) return url

    if (isMobilePdfBrowser()) {
      return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`
    }
    return url
  }

  useEffect(() => {
    if (!shouldUseNativeIOSPreview || !downloadTarget || didOpenNativePreviewRef.current) return

    didOpenNativePreviewRef.current = true
    ;(window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'OPEN_PDF_PREVIEW',
        url: downloadTarget,
        file_name: getResolvedFileName(),
        auth_token: getNativeAppAuthToken(),
      })
    )
    onBack()
  }, [downloadTarget, onBack, shouldUseNativeIOSPreview])

  useEffect(() => {
    let isMounted = true
    let objectUrl = ''

    if (shouldUseNativeIOSPreview) {
      setViewerUrl('')
      setViewerLoadFailed(false)
      setIsViewerLoading(false)
      return
    }

    if (!viewerSource) {
      setViewerUrl('')
      setViewerLoadFailed(false)
      setIsViewerLoading(false)
      return
    }

    if (
      viewerSource.startsWith('blob:') ||
      viewerSource.startsWith('data:') ||
      (!shouldUseIOSDataUrlPreview && isMobilePdfBrowser()) ||
      (!shouldUseIOSDataUrlPreview && viewerSource.includes('drive.google.com'))
    ) {
      setViewerUrl(viewerSource)
      setViewerLoadFailed(false)
      setIsViewerLoading(false)
      return
    }

    const loadPdfPreview = async () => {
      setIsViewerLoading(true)
      setViewerLoadFailed(false)

      try {
        const pdfBlob = loadPdfPreviewBlob
          ? ensurePdfBlob(await loadPdfPreviewBlob(viewerSource))
          : await fetchPdfBlob(viewerSource)
        const nextViewerUrl = shouldUseIOSDataUrlPreview
          ? await blobToDataUrl(pdfBlob)
          : URL.createObjectURL(pdfBlob)

        objectUrl = shouldUseIOSDataUrlPreview ? '' : nextViewerUrl
        if (!isMounted) return

        setViewerUrl(nextViewerUrl)
      } catch (error) {
        if (!isMounted) return

        console.error('Unable to load PDF preview, falling back to direct URL.', error)
        setViewerLoadFailed(shouldUseIOSDataUrlPreview)
        setViewerUrl(shouldUseIOSDataUrlPreview ? '' : viewerSource)
      } finally {
        if (isMounted) {
          setIsViewerLoading(false)
        }
      }
    }

    void loadPdfPreview()

    return () => {
      isMounted = false
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [loadPdfPreviewBlob, shouldUseIOSDataUrlPreview, shouldUseNativeIOSPreview, viewerSource])

  const handleDownload = async () => {
    if (!downloadTarget || isDownloading) return

    const resolvedFileName = getResolvedFileName()

    if (onDownload) {
      setIsDownloading(true)
      try {
        const handled = await onDownload({url: downloadTarget, fileName: resolvedFileName})
        if (handled !== false) return
      } catch (error) {
        console.error('Unable to download PDF via custom handler.', error)
        return
      } finally {
        setIsDownloading(false)
      }
    }

    if ((window as any).ReactNativeWebView && isIOSPdfBrowser()) {
      setIsDownloading(true)
      try {
        await sendPdfBytesToNativeApp(downloadTarget, resolvedFileName)
        return
      } catch (error) {
        console.error('Unable to send PDF bytes to native app, falling back to URL download.', error)
        openDirectPdf(downloadTarget, resolvedFileName)
        return
      } finally {
        setIsDownloading(false)
      }
    }

    if (isMobilePdfBrowser() && !downloadTarget.startsWith('blob:')) {
      openDirectPdf(downloadTarget, resolvedFileName)
      return
    }

    setIsDownloading(true)
    let objectUrl = ''
    try {
      const pdfBlob = await fetchPdfBlob(downloadTarget)

      try {
        saveAs(pdfBlob, resolvedFileName)
        return
      } catch (saveError) {
        console.error(
          'Unable to save PDF via file-saver, falling back to object URL download.',
          saveError
        )
      }

      objectUrl = URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = resolvedFileName
      link.rel = 'noopener noreferrer'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return
    } catch (error) {
      console.error('Unable to download PDF via fetch, falling back to direct URL.', error)
      openDirectPdf(downloadTarget, resolvedFileName)
    } finally {
      if (objectUrl) {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
      }
      setIsDownloading(false)
    }
  }

  return createPortal(
    <div className='fixed inset-0 z-[9999] flex h-[100dvh] flex-col bg-white'>
      <div className='relative z-[10001] flex flex-nowrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 pb-2 pt-[calc(env(safe-area-inset-top,0px)+0.5rem)] shadow-sm md:px-6 md:pb-3 md:pt-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <button
            type='button'
            onClick={onBack}
            className='inline-flex min-h-10 shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50'
            aria-label='Back to previous screen'
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <div className='hidden min-w-0 sm:block'>
            <div className='truncate text-sm font-semibold text-gray-900 md:text-base'>
              {fileName || 'PDF Viewer'}
            </div>
          </div>
        </div>

        <button
          type='button'
          onClick={() => void handleDownload()}
          disabled={isDownloading}
          className='inline-flex min-h-10 shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'
          aria-label={isDownloading ? 'Downloading PDF' : 'Download PDF'}
        >
          <Download size={16} />
          <span className='hidden sm:inline'>{isDownloading ? 'Downloading...' : 'Download'}</span>
        </button>
      </div>

      <div className='min-h-0 flex-1 bg-[#111827]'>
        {isViewerLoading ? (
          <div className='flex h-full items-center justify-center text-sm font-medium text-white'>
            Loading PDF...
          </div>
        ) : viewerLoadFailed || !viewerUrl ? (
          <div className='flex h-full flex-col items-center justify-center gap-3 px-4 text-center text-white'>
            <div className='text-sm font-semibold'>Unable to preview this PDF.</div>
            <div className='text-xs text-white/70'>Use Download to open the file locally.</div>
          </div>
        ) : (
          <iframe
            src={getPdfViewerUrl(viewerUrl)}
            title={fileName || 'PDF Viewer'}
            className='block h-full w-full border-0'
            allow={iframeAllow}
          />
        )}
      </div>
    </div>,
    portalRoot
  )
}

export default PDFWebview
